/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
// react-routers components
// import { Link } from "react-router-dom";

// prop-types is library for typechecking of props
import PropTypes from "prop-types";

// Styles
import "./styles.css";
// React
import { useEffect, useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// ExcelJS for exporting data
import ExcelJS from "exceljs";
import { saveAs } from "file-saver"; // To save the file on the client side

function YearEndInvoiceSprayListGrid({ title, invoiceData, shadow, startDate, endDate }) {
  const [rows, setRows] = useState([]);

  function organizeSprayData(data) {
    const groupedByOrchard = {};

    // Step 1: Group by orchard and then chemical
    data.forEach((item) => {
      const { orchard } = item;
      const chemical = item.chemicalName;

      if (!groupedByOrchard[orchard]) groupedByOrchard[orchard] = {};
      if (!groupedByOrchard[orchard][chemical]) {
        groupedByOrchard[orchard][chemical] = {
          ...item,
          totalused: parseFloat(item.totalused) || 0,
          chemicalcost: item.chemicalcost || 0,
          billableCost: parseFloat(item.billableCost?.replace("$", "")) || 0,
          tenPercent: parseFloat(item.tenPercent?.replace("$", "")) || 0,
        };
      } else {
        groupedByOrchard[orchard][chemical].totalused += parseFloat(item.totalused) || 0;
        groupedByOrchard[orchard][chemical].chemicalcost += item.chemicalcost || 0;
        groupedByOrchard[orchard][chemical].billableCost +=
          parseFloat(item.billableCost?.replace("$", "")) || 0;
        groupedByOrchard[orchard][chemical].tenPercent +=
          parseFloat(item.tenPercent?.replace("$", "")) || 0;
      }
    });

    // Step 2: Flatten with orchard label only on the first line
    const result = [];

    Object.entries(groupedByOrchard).forEach(([orchard, chems]) => {
      const orchardTotals = {
        orchard: "Total Cost",
        chemicalcost: 0,
        totalused: 0,
        billableCost: 0,
        tenPercent: 0,
      };

      const chemEntries = Object.values(chems);
      chemEntries.forEach((entry, index) => {
        const row = {
          ...entry,
          orchard: index === 0 ? orchard : "-",
          totalused: `${entry.totalused.toFixed(2)} Kilogram`,
          billableCost: `$${entry.billableCost.toFixed(2)}`,
          tenPercent: `$${entry.tenPercent.toFixed(2)}`,
        };
        result.push(row);

        orchardTotals.chemicalcost += entry.chemicalcost;
        orchardTotals.totalused += parseFloat(entry.totalused);
        orchardTotals.billableCost += parseFloat(entry.billableCost);
        orchardTotals.tenPercent += parseFloat(entry.tenPercent);
      });

      result.push({
        id: `${orchard}-total`,
        orchard: `${orchard}-Total Cost`,
        chemicalName: "",
        cost: "",
        date: "",
        color: "",
        totalused: `${orchardTotals.totalused.toFixed(2)} Kilogram`,
        hectares: "",
        chemicalcost: orchardTotals.chemicalcost,
        billableCost: `$${orchardTotals.billableCost.toFixed(2)}`,
        tenPercent: `$${orchardTotals.tenPercent.toFixed(2)}`,
      });
    });

    return result;
  }

  useEffect(() => {
    if (invoiceData?.length > 0) {
      const mapped = invoiceData.map((spray) => {
        const chemicalcost = Math.round(Number(spray.chemicalcost) * 100) / 100 || 0; // Round to 2 decimals
        const tenPercent = `$${(chemicalcost * 0.1).toFixed(2)}`;
        const billableCost = `$${(chemicalcost * 1.1).toFixed(2)}`;
        return { ...spray, tenPercent, billableCost };
      });
      const organizedData = organizeSprayData(mapped);
      setRows(organizedData);
    } else {
      setRows([]);
    }
  }, [invoiceData]);

  const columns = [
    { field: "orchard", headerName: "Orchard", width: 250 },
    { field: "chemicalName", headerName: "Chemical", width: 200 },
    { field: "totalused", headerName: "Total Used", width: 200 },
    { field: "chemicalcost", headerName: "Chem Cost", width: 110 },
    { field: "tenPercent", headerName: "10%", width: 110 },
    { field: "billableCost", headerName: "Billable", width: 110 },
  ];

  const TimestamptoNZDate = (timestamp) => {
    // Convert to milliseconds
    const milliseconds = timestamp.seconds * 1000 + timestamp.nanoseconds / 1e6;

    // Create a Date object
    const date = new Date(milliseconds);

    // Format to "Sept 8 2024" in New Zealand Time (NZT)
    const options = { year: "numeric", month: "short", day: "numeric" };
    const nztDate = date.toLocaleDateString("en-NZ", { ...options, timeZone: "Pacific/Auckland" });

    return nztDate;
  };
  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Invoice Data");

    // Add columns to the worksheet (use column headers)
    worksheet.columns = columns.map((col) => ({
      header: col.headerName,
      key: col.field,
      width: 20,
    }));

    // Apply bottom border to the header
    worksheet.getRow(1).eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { vertical: "middle", horizontal: "center" };
      cell.border = {
        bottom: { style: "thick", color: { argb: "FF000000" } }, // Black bottom border
      };
    });

    // Add rows to the worksheet
    rows.forEach((row) => {
      const excelRow = worksheet.addRow(row);

      // Change background color for first row of each orchard
      if (row.orchard !== "--" && !row.orchard.includes("Total")) {
        excelRow.eachCell((cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "FF000000" } }, // Black bottom border
          };

          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFE5E5E5" }, // Light gray background for first orchard row
          };
        });
      }

      // Change background color for the total of each orchard
      if (row.orchard.includes("Total")) {
        excelRow.eachCell((cell) => {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "b4eeb4" }, // Light gray background for first orchard row
          };
        });
      }

      // Additional styling based on row conditions (like unBilled rows)
      if (row.unBilled) {
        excelRow.eachCell((cell) => {
          cell.font = { color: { argb: "FFFF0000" } }; // Red font for unBilled rows
        });
      }
    });

    // Make the header row sticky by freezing it
    worksheet.views = [
      { state: "frozen", ySplit: 1 }, // Freeze the first row
    ];

    // Save the Excel file
    workbook.xlsx.writeBuffer().then((buffer) => {
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const fileName = `${TimestamptoNZDate(startDate)} - ${TimestamptoNZDate(
        endDate
      )} Invoices.xlsx`;
      saveAs(blob, fileName);
    });
  };

  const getCellClassName = (params) => {
    const { field, row } = params;
    if (
      ["chemicalName", "totalused", "cost", "chemicalcost", "hectares", "spraycost"].includes(
        field
      ) &&
      row.unBilled
    ) {
      return "unbilled-cell";
    }
    if (["orchard", "billablecost"].includes(field) && row.orchard.includes("Total")) {
      return "total-cell";
    }
    return "";
  };

  return (
    <Card sx={{ height: "100%", boxShadow: !shadow && "none" }}>
      <MDBox pt={4} px={2} pb={3}>
        <MDTypography variant="h4" fontWeight="medium" textTransform="capitalize">
          {title}
        </MDTypography>
        <MDButton
          color="primary"
          // disabled={!table.getIsSomeRowsSelected()}
          onClick={exportToExcel}
          variant="contained"
        >
          Download Excel File
        </MDButton>
      </MDBox>
      <DataGrid
        rows={rows}
        columns={columns}
        getCellClassName={getCellClassName}
        slots={{ toolbar: GridToolbar }}
      >
        {" "}
      </DataGrid>
    </Card>
  );
}

// Setting default props for the ProfilesList
YearEndInvoiceSprayListGrid.defaultProps = {
  shadow: true,
};

// Typechecking props for the ProfilesList
YearEndInvoiceSprayListGrid.propTypes = {
  title: PropTypes.string.isRequired,
  shadow: PropTypes.bool,
  invoiceData: PropTypes.objectOf.isRequired,
};

export default YearEndInvoiceSprayListGrid;
